/**
 * Open a KPI citation in the viewer the chat uses.
 *
 * Workbooks open in the `SpreadsheetViewer` modal — the popup, with the cited
 * cell highlighted and scrolled to. PDFs open in the `PdfViewer` modal at the
 * cited page. Either falls back to a signed URL in a new tab when its viewer
 * is not mounted.
 *
 * Returns false when there was nothing to open, so a caller can fall back to
 * showing the citation rather than swallowing the click.
 */

import { useCallback } from "react";

import { usePdfViewer } from "../../context/PdfViewerContext";
import { useSpreadsheetViewer } from "../../context/SpreadsheetViewerContext";
import type { KpiSourceRef } from "../../types/kpi";
import { openDopplerUri } from "../../utils/dopplerUri";
import {
	type KpiCitedNumber,
	kpiSourceHref,
	kpiSourceKind,
} from "./kpiSourceUri";

export type OpenKpiSource = (
	source: KpiSourceRef | null | undefined,
	/** The number the source is opened for, so a PDF can highlight it. */
	cited?: KpiCitedNumber | null,
) => boolean;

export function useKpiSourceOpener(): OpenKpiSource {
	const viewer = useSpreadsheetViewer();
	const pdfViewer = usePdfViewer();

	return useCallback(
		(source, cited) => {
			if (!source) return false;
			const href = kpiSourceHref(source, cited);
			if (href === null) return false;

			const kind = kpiSourceKind(source);
			if (kind === "excel") {
				const opened =
					viewer?.openSpreadsheetHref(href, { filename: source.file }) ?? false;
				if (opened) return true;
			}
			if (kind === "pdf") {
				const opened =
					pdfViewer?.openPdfHref(href, { filename: source.file }) ?? false;
				if (opened) return true;
			}
			void openDopplerUri(href);
			return true;
		},
		[viewer, pdfViewer],
	);
}
